// ABOUTME: The application process section of the feed (PJL): the applications block with a filter row above it, running,
// ABOUTME: needs you or ended, and „Ausgeblendete anzeigen“, which reads the list again with the rows the talent hid.
import { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Text } from 'components/Text';
import { ApplicationsBlock } from 'modules/applications/components/ApplicationsBlock';
import { useApplicationsWithHiddenQuery } from 'modules/applications/query';
import {
  TalentApplication,
  TalentApplicationStatus
} from 'modules/applications/types';
import { getTrans, TranslationKey } from 'modules/i18n';
import { ApplicationProcessSection } from '../types';

type StatusFilter = 'all' | 'running' | 'needsYou' | 'ended';

const FILTERS: { key: StatusFilter; label: TranslationKey }[] = [
  { key: 'all', label: 'applications.filter.all' },
  { key: 'running', label: 'applications.filter.running' },
  { key: 'needsYou', label: 'applications.filter.needsYou' },
  { key: 'ended', label: 'applications.filter.ended' }
];

const matches = (application: TalentApplication, filter: StatusFilter) => {
  const running = application.status === TalentApplicationStatus.ACTIVE;
  if (filter === 'running') return running;
  if (filter === 'needsYou') return !!application.needsAction;
  if (filter === 'ended') return !running;
  return true;
};

export const ApplicationProcess = ({
  section,
  now,
  canHide
}: {
  section: ApplicationProcessSection;
  now: Date;
  canHide: boolean;
}) => {
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [showHidden, setShowHidden] = useState(false);
  const withHidden = useApplicationsWithHiddenQuery(showHidden);
  const source =
    showHidden && withHidden.data ? withHidden.data : section.items;
  const applications = source.filter(application =>
    matches(application, filter)
  );

  return (
    <Box
      component="section"
      aria-label={getTrans('applications.block.title')}
      data-testid="feed-application-process"
    >
      {canHide && (
        <Box
          role="group"
          aria-label={getTrans('applications.filter.label')}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.75,
            mb: 1
          }}
        >
          {FILTERS.map(({ key, label }) => (
            <Chip
              key={key}
              size="small"
              clickable
              component="button"
              data-testid={`applications-filter-${key}`}
              color={filter === key ? 'primary' : 'default'}
              variant={filter === key ? 'filled' : 'outlined'}
              label={getTrans(label)}
              onClick={() => setFilter(key)}
            />
          ))}
          <FormControlLabel
            sx={{ ml: 'auto', mr: 0 }}
            control={
              <Switch
                size="small"
                checked={showHidden}
                onChange={(_, checked) => setShowHidden(checked)}
                inputProps={{
                  'aria-label': getTrans('applications.filter.showHidden')
                }}
              />
            }
            label={
              <Text variant="caption">
                {getTrans('applications.filter.showHidden')}
              </Text>
            }
          />
        </Box>
      )}
      {applications.length ? (
        <ApplicationsBlock
          applications={applications}
          now={now}
          canHide={canHide}
          feedOpen={!section.collapsed}
        />
      ) : (
        <Text
          variant="body2"
          color="text.secondary"
          component="p"
          data-testid="applications-filter-empty"
          sx={{ mb: 2 }}
        >
          {getTrans('applications.filter.empty')}
        </Text>
      )}
    </Box>
  );
};
